import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { centsToDollars } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { getFetchError } from '@/reducers/fetch'

import { UserCoupon, UserCouponProductName } from './userCoupons.slice'
import {
  ADMIN_REMOVE_COUPON_KEY,
  adminRemoveCoupon,
  fetchAdminUserCoupons,
} from './adminUserCoupons.actions'
import { formatCouponDuration } from './userCouponDuration.helpers'
import { Alert, Button, Card, CardColors, Checkbox, Table, Text } from '@/ui'

import AdminAddCouponModal from './AdminAddCouponModal'
import { TypographyStyles } from '@/styles'

const CouponTable = ({
  coupons,
  deleteCoupon,
}: {
  coupons: UserCoupon[]
  deleteCoupon: (userCouponId: number) => void
}) => {
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>Type</Table.HeaderCell>
          <Table.HeaderCell>Discount</Table.HeaderCell>
          <Table.HeaderCell>Duration</Table.HeaderCell>
          <Table.HeaderCell>Redeemed</Table.HeaderCell>
          <Table.HeaderCell>Actions</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {coupons.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={5}>No coupons found</Table.Cell>
          </Table.Row>
        )}
        {coupons.map((coupon, index) => (
          <Table.Row key={`${coupon.id}-${index}`}>
            {
              <Table.Cell>
                {coupon.productName !== UserCouponProductName.taxAdvisory
                  ? coupon.productName
                  : `${coupon.productName} - ${coupon.metadata?.durationInMinutes} minutes`}
              </Table.Cell>
            }
            <Table.Cell>
              {coupon.amountOff
                ? `$${centsToDollars(coupon.amountOff)}`
                : coupon.percentOff
                  ? `${coupon.percentOff}%`
                  : 'N/A'}
            </Table.Cell>
            <Table.Cell>{formatCouponDuration(coupon)}</Table.Cell>
            <Table.Cell>
              {coupon.redeemedAt
                ? DateTime.fromISO(coupon.redeemedAt).toFormat(
                    DATE_FORMATS_LUXON.DISPLAY_FULL
                  )
                : ''}
            </Table.Cell>
            <Table.Cell>
              {!coupon.redeemedAt && (
                <Button onClick={() => deleteCoupon(coupon.id)} variant="link">
                  Remove
                </Button>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  )
}

const AdminUserCouponAssignment = ({ userId }: { userId: number }) => {
  const dispatch = useAppDispatch()
  const [, setOpenModal] = useState(false)
  const [showRedeemed, setShowRedeemed] = useState(false)
  const [couponsForUser, setCouponsForUser] = useState<UserCoupon[]>([])
  const unredeemedCoupons = useMemo(
    () => couponsForUser.filter((c) => !c.redeemedAt),
    [couponsForUser]
  )
  const deleteError = useReselector(getFetchError, ADMIN_REMOVE_COUPON_KEY)

  const deleteCoupon = useCallback(
    async (userCouponId: number) => {
      const res = await adminRemoveCoupon({ userId, userCouponId })(dispatch)
      if (res?.deletedId) {
        setCouponsForUser([
          ...couponsForUser.filter((coupon) => coupon.id !== res.deletedId),
        ])
      }
    },
    [couponsForUser, dispatch, userId]
  )

  const closeModal = useCallback(
    (newCoupon?: UserCoupon) => {
      // add coupon
      if (newCoupon?.id) {
        setCouponsForUser([...couponsForUser, newCoupon])
      }
      setOpenModal(false)
    },
    [couponsForUser]
  )

  useEffect(() => {
    const loadCoupons = async () => {
      const res = await fetchAdminUserCoupons(userId, {
        includeRedeemed: true,
      })(dispatch)
      if (res) {
        setCouponsForUser(res)
      }
    }

    loadCoupons()
  }, [dispatch, userId])
  return (
    <Card color={CardColors.LEVEL_0} className="flex flex-col gap-4 p-6 w-full">
      <Text variant={TypographyStyles.H2}>User Coupons</Text>
      <Checkbox
        label="Show Redeemed"
        checked={showRedeemed}
        onCheckedChange={() => setShowRedeemed(!showRedeemed)}
      />
      <CouponTable
        deleteCoupon={deleteCoupon}
        coupons={showRedeemed ? couponsForUser : unredeemedCoupons}
      />
      {deleteError?.message && (
        <Alert variant="error" description={deleteError.message} />
      )}

      <AdminAddCouponModal userId={userId} close={closeModal} />
    </Card>
  )
}

export default AdminUserCouponAssignment
